import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery } from "@apollo/client";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { GET_USER_ACCOUNT_TOKENS } from "../../graphql/queries";
import { API_BASE } from "../../utils/api/api";
import styles from "./ConnectModal.module.css";

const TIKTOK_APP_ID     = import.meta.env.VITE_TIKTOK_APP_ID;
const TIKTOK_BIZ_APP_ID = import.meta.env.VITE_TIKTOK_BIZ_APP_ID || "7618852737266778129";

// TikTok brand icon (official path)
function TikTokIcon({ size = 24, color = "#010101" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V9.28a8.27 8.27 0 004.84 1.56V7.38a4.85 4.85 0 01-1.07-.69z"
        fill={color}
      />
    </svg>
  );
}

// Reusable hook that handles the full OAuth popup + polling flow
function useOAuthConnect({ authToken, userId, startUrl, tokenKey, tokensData, refetchTokens, visible, onConnected, onClose, appIdEnvVar, appIdEnvName }) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [status,       setStatus]       = useState(null);
  const pollIntervalRef = useRef(null);
  const oauthWindowRef  = useRef(null);

  const isConnected = Boolean(tokensData?.getUserAccountTokens?.[tokenKey]);

  useEffect(() => {
    if (!visible || !isConnecting) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const poll = async () => {
      try {
        const result = await refetchTokens();
        if (Boolean(result?.data?.getUserAccountTokens?.[tokenKey])) {
          setIsConnecting(false);
          setStatus("Connected!");
          onConnected?.();
          setTimeout(() => onClose?.(), 1500);
        }
      } catch (e) {
        console.log(`[TikTokConnectModal:${tokenKey}] refetch error:`, e?.message || e);
      }
    };

    poll();
    pollIntervalRef.current = setInterval(poll, 3000);
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [visible, isConnecting, refetchTokens, onClose, onConnected, tokenKey]);

  useEffect(() => {
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, []);

  const startOAuth = useCallback(async () => {
    if (!userId || !authToken) {
      setStatus("User session missing. Please refresh and try again.");
      return;
    }
    if (!appIdEnvVar) {
      setStatus(`App ID (${appIdEnvName}) is missing. Please contact support.`);
      return;
    }

    setIsConnecting(true);
    setStatus("Opening TikTok login…");

    oauthWindowRef.current = window.open("about:blank", "_blank", "width=600,height=700");

    if (!oauthWindowRef.current) {
      setIsConnecting(false);
      setStatus("Popup blocked. Allow popups for this site and try again.");
      return;
    }

    try {
      const response = await fetch(startUrl, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.authUrl) {
        throw new Error(payload?.error || "Failed to start TikTok OAuth");
      }
      if (oauthWindowRef.current) {
        oauthWindowRef.current.location.replace(payload.authUrl);
      } else {
        window.open(payload.authUrl, "_blank", "width=600,height=700");
      }
    } catch (err) {
      if (oauthWindowRef.current && !oauthWindowRef.current.closed) oauthWindowRef.current.close();
      oauthWindowRef.current = null;
      setIsConnecting(false);
      setStatus(err?.message || "Failed to start TikTok connection.");
      return;
    }

    const checkClosed = setInterval(() => {
      if (oauthWindowRef.current?.closed) {
        clearInterval(checkClosed);
        setTimeout(() => {
          if (!isConnected) setStatus("Checking connection status…");
        }, 800);
      }
    }, 500);
  }, [authToken, userId, startUrl, appIdEnvVar, appIdEnvName, isConnected]);

  return { isConnecting, status, isConnected, startOAuth };
}

const TikTokConnectModal = ({ visible, onClose, onConnected }) => {
  const { user } = useAuthUser();
  const authToken = user?.token;
  const userId    = user?._id || user?.id || user?.userId;

  const { data: tokensData, refetch: refetchTokens } = useQuery(
    GET_USER_ACCOUNT_TOKENS,
    {
      skip: !authToken,
      fetchPolicy: "network-only",
      context: {
        headers: { Authorization: authToken ? `Bearer ${authToken}` : "" },
      },
    }
  );

  const sharedProps = { authToken, userId, tokensData, refetchTokens, visible, onConnected, onClose };

  const creator = useOAuthConnect({
    ...sharedProps,
    startUrl:     `${API_BASE}/tiktok/start`,
    tokenKey:     "tiktokToken",
    appIdEnvVar:  TIKTOK_APP_ID,
    appIdEnvName: "VITE_TIKTOK_APP_ID",
  });

  const business = useOAuthConnect({
    ...sharedProps,
    startUrl:     `${API_BASE}/tiktok-business/start`,
    tokenKey:     "tiktokBusinessToken",
    appIdEnvVar:  TIKTOK_BIZ_APP_ID,
    appIdEnvName: "VITE_TIKTOK_BIZ_APP_ID",
  });

  if (!visible) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose} type="button">
          &times;
        </button>

        {/* Icon */}
        <div className={styles.iconBox} style={{ backgroundColor: "#f0f0f0" }}>
          <TikTokIcon size={26} color="#010101" />
        </div>

        <h2 className={styles.title}>Connect TikTok</h2>
        <p className={styles.subtitle}>
          Connect your TikTok accounts to unlock content and ads analytics,
          audience insights, and AI-powered growth recommendations.
        </p>

        {/* ── TikTok Creator ── */}
        <div className={styles.sectionLabel} style={{ marginTop: 16, marginBottom: 6, fontWeight: 600, fontSize: "0.8rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Creator / Profile
        </div>
        <button
          className={`${styles.primaryBtn} ${styles.tiktokBtn}`}
          onClick={creator.startOAuth}
          disabled={creator.isConnecting}
        >
          <TikTokIcon size={18} color="#ffffff" />
          <span style={{ marginLeft: 8 }}>
            {creator.isConnecting ? "Connecting…" : creator.isConnected ? "TikTok Connected" : "Connect TikTok"}
          </span>
        </button>

        {(creator.status || creator.isConnecting) && (
          <div className={styles.statusRow}>
            {creator.isConnecting && <div className={styles.spinner} />}
            {creator.status && <span className={styles.statusText}>{creator.status}</span>}
          </div>
        )}

        {creator.isConnected && (
          <div className={styles.connectedBadge}>
            <svg viewBox="0 0 24 24" fill="#166534"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
            TikTok profile connected
          </div>
        )}

        {/* ── TikTok Business / Ads ── */}
        <div className={styles.sectionLabel} style={{ marginTop: 20, marginBottom: 6, fontWeight: 600, fontSize: "0.8rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Ads / Business
        </div>
        <button
          className={`${styles.primaryBtn} ${styles.tiktokBtn}`}
          onClick={business.startOAuth}
          disabled={business.isConnecting}
        >
          <TikTokIcon size={18} color="#ffffff" />
          <span style={{ marginLeft: 8 }}>
            {business.isConnecting ? "Connecting…" : business.isConnected ? "TikTok Ads Connected" : "Connect TikTok Ads"}
          </span>
        </button>

        {(business.status || business.isConnecting) && (
          <div className={styles.statusRow}>
            {business.isConnecting && <div className={styles.spinner} />}
            {business.status && <span className={styles.statusText}>{business.status}</span>}
          </div>
        )}

        {business.isConnected && (
          <div className={styles.connectedBadge}>
            <svg viewBox="0 0 24 24" fill="#166534"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
            TikTok Ads connected
          </div>
        )}

        <div className={styles.tipBox} style={{ marginTop: 16 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#1D4ED8">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
          <span className={styles.tipText}>
            <strong>Creator</strong> connects your profile, follower stats, and video performance.
            <br />
            <strong>Ads</strong> connects your TikTok advertiser accounts for campaign spend, ROAS, and audience insights.
          </span>
        </div>
      </div>
    </div>
  );
};

export default TikTokConnectModal;
